import { useCallback, useEffect, useRef } from 'react';

import Select from '@cartwhl/ui/atoms/Select';
import Text from '@cartwhl/ui/base/Text';
import type {
  FilesListFile,
  FilesListHandle,
} from '@cartwhl/ui/input/FilesList';

import { syncMediaUploadThumbnailJobs } from '../../shared/mediaUpload/mediaUploadThumbnailCache';
import BatchReferenceAddMenu from '../../shared/BatchReferenceAddMenu';
import { useStore } from '../batchMascotUploadStore';
import { useMascotRowLibraryPickers } from '../hooks/useMascotRowLibraryPickers';
import {
  buildMultiReferenceItems,
  buildSingleReferenceItems,
} from '../mascotReferenceItems';
import type { MascotRow } from '../types';
import MascotReferenceField from './MascotReferenceField';
import { ReferenceFieldLabel } from './MascotReferenceField';
import MascotReferenceFilledPanel from './MascotReferenceFilledPanel';

const VIDEO_MIME_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];
const IMAGE_MIME_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];

const CHARACTER_COUNT_OPTIONS = [
  { value: 'single', label: 'Single mascot' },
  { value: 'multi', label: 'Multi-mascot' },
];

type MascotRowSectionProps = {
  row: MascotRow;
};

export default function MascotRowSection({ row }: MascotRowSectionProps) {
  const store = useStore();
  const videoRef = useRef<FilesListHandle>(null);
  const charRefsRef = useRef<FilesListHandle>(null);
  const envRef = useRef<FilesListHandle>(null);

  const { motionPicker, characterPicker, backgroundPicker } =
    useMascotRowLibraryPickers(row, store);

  useEffect(() => {
    store.registerRowFilesListRefs(row.id, {
      getVideoFiles: () => videoRef.current?.getFiles() ?? [],
      getCharRefFiles: () => charRefsRef.current?.getFiles() ?? [],
      getEnvFiles: () => envRef.current?.getFiles() ?? [],
    });
    return () => store.unregisterRowFilesListRefs(row.id);
  }, [row.id, store]);

  const syncVideoCount = useCallback(
    (files: FilesListFile[]) => {
      syncMediaUploadThumbnailJobs(files);
      store.updateMascotRow(
        row.id,
        'videoFileCount',
        files.length + (row.libraryMotionRef ? 1 : 0),
      );
    },
    [row.id, row.libraryMotionRef, store],
  );

  const syncCharacterCount = useCallback(
    (files: FilesListFile[]) => {
      syncMediaUploadThumbnailJobs(files);
      store.updateMascotRow(
        row.id,
        'characterRefFilesCount',
        files.length + row.libraryCharRefs.length,
      );
    },
    [row.id, row.libraryCharRefs.length, store],
  );

  const syncBackgroundCount = useCallback(
    (files: FilesListFile[]) => {
      syncMediaUploadThumbnailJobs(files);
      store.updateMascotRow(
        row.id,
        'environmentFileCount',
        files.length + (row.libraryBackgroundRef ? 1 : 0),
      );
    },
    [row.id, row.libraryBackgroundRef, store],
  );

  const renderMotionFilled = useCallback(
    (files: FilesListFile[], removeFile: (fileId: string) => void) => (
      <MascotReferenceFilledPanel
        items={buildSingleReferenceItems({
          librarySelection: row.libraryMotionRef,
          assetType: 'MEDIA_VIDEO',
          onRemoveLibrary: () => store.setRowLibraryMotionRef(row.id, null),
          localFile: files[0],
          onRemoveLocal: removeFile,
        })}
      />
    ),
    [row.id, row.libraryMotionRef, store],
  );

  const renderCharacterFilled = useCallback(
    (
      files: FilesListFile[],
      removeFile: (fileId: string) => void,
      addMore?: () => void,
    ) => (
      <MascotReferenceFilledPanel
        items={buildMultiReferenceItems({
          librarySelections: row.libraryCharRefs,
          assetType: 'MEDIA_IMAGE',
          onRemoveLibrary: (mediaID) =>
            store.removeRowLibraryCharRef(row.id, mediaID),
          localFiles: files,
          onRemoveLocal: removeFile,
        })}
        floatingAction={
          addMore ? (
            <BatchReferenceAddMenu
              layout="floating"
              onAddFromLibrary={() => characterPicker.openPicker()}
              onAddFromComputer={addMore}
            />
          ) : undefined
        }
      />
    ),
    [characterPicker, row.id, row.libraryCharRefs, store],
  );

  const renderBackgroundFilled = useCallback(
    (files: FilesListFile[], removeFile: (fileId: string) => void) => (
      <MascotReferenceFilledPanel
        items={buildSingleReferenceItems({
          librarySelection: row.libraryBackgroundRef,
          assetType: 'MEDIA_IMAGE',
          onRemoveLibrary: () => store.setRowLibraryBackgroundRef(row.id, null),
          localFile: files[0],
          onRemoveLocal: removeFile,
        })}
      />
    ),
    [row.id, row.libraryBackgroundRef, store],
  );

  return (
    <div className="flex flex-col">
      {motionPicker.modal}
      {characterPicker.modal}
      {backgroundPicker.modal}

      <div className="flex flex-col gap-2">
        <Text variant="studio/body/medium/sm" className="text-secondary">
          Mascots to animate
        </Text>
        <Select
          items={CHARACTER_COUNT_OPTIONS}
          value={row.characterCount}
          onValueChange={(value) =>
            store.updateMascotRow(
              row.id,
              'characterCount',
              value as 'single' | 'multi',
            )
          }
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:items-start">
        <MascotReferenceField
          label={<ReferenceFieldLabel>Motion reference</ReferenceFieldLabel>}
          filesListRef={videoRef}
          allowedMimeTypes={VIDEO_MIME_TYPES}
          maxFiles={1}
          hasContent={row.videoFileCount > 0}
          disableClick={motionPicker.isOpen}
          onFilesChange={syncVideoCount}
          onOpenLibrary={() => motionPicker.openPicker()}
          renderFilled={renderMotionFilled}
        />

        <MascotReferenceField
          label={
            <ReferenceFieldLabel>Character reference(s)</ReferenceFieldLabel>
          }
          filesListRef={charRefsRef}
          allowedMimeTypes={IMAGE_MIME_TYPES}
          hasContent={row.characterRefFilesCount > 0}
          supportsAddMore
          disableClick={characterPicker.isOpen}
          onFilesChange={syncCharacterCount}
          onOpenLibrary={() => characterPicker.openPicker()}
          renderFilled={renderCharacterFilled}
        />

        <MascotReferenceField
          label={
            <ReferenceFieldLabel>
              Background reference{' '}
              <span className="text-tertiary">(optional)</span>
            </ReferenceFieldLabel>
          }
          filesListRef={envRef}
          allowedMimeTypes={IMAGE_MIME_TYPES}
          maxFiles={1}
          hasContent={row.environmentFileCount > 0}
          disableClick={backgroundPicker.isOpen}
          onFilesChange={syncBackgroundCount}
          onOpenLibrary={() => backgroundPicker.openPicker()}
          renderFilled={renderBackgroundFilled}
        />
      </div>
    </div>
  );
}
